import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Button,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
  GridRowModel,
} from '@mui/x-data-grid';
import React, { FC, useEffect, useState, useMemo } from 'react';
import { useStyles } from './ApplicationTrackingStyles';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useNavigate } from 'react-router-dom';
import {
  selectGetAllApplicationsData,
  postGetAllApplications,
  selectGetAllApplicationsLoading,
  selectGetAllApplicationsStale,
  selectGetAllApplicationsLastFetchSize,
} from '../../service/application/ApplicationListSlice';
import Toast from '../../component/Toast';
import Chip from '../../component/Chip';
import {
  dollarFormatter,
  getStatusColor,
  getSupplierStatusColor,
  hasAdminPermission,
  hasMemberPermission,
  hasPractitionerPermission,
  isDualRole,
} from '../../helper/AppHelper';
import MobileGrid from '../../component/MobileGrid/MobileGrid';
import { TabPanel } from '../../component/TabPanel';
import { Status } from '../../service/application/ApplicationModels';
import {
  searchKeyString,
  selectSearchApplicationData,
  selectSearchApplicationLoading,
} from '../../service/application/SearchSlice';
import {
  selectActiveTab,
  selectTabPages,
  selectTabPageSizes,
  setActiveTab,
  setPageForTab,
  setPageSizeForTab,
} from '../../service/application/ApplicationTrackingUISlice';

const filterList = ['All'];

const ApplicationTracking: FC<{}> = ({}) => {
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [approvedApplications, setApprovedApplications] = useState([]);
  const [closedApplications, setClosedApplications] = useState([]);
  const [rejectedApplications, setRejectedApplications] = useState([]);
  const [allApplications, setAllApplications] = useState([]);
  // Derived queues for dual-role users
  const activeTab = useAppSelector(selectActiveTab);
  // Removed unused activeTabProgress state
  const [open, setOpen] = useState(false);
  // Track pageSize per tab
  // Removed unused tabCount
  const tabPageSizes = useAppSelector(selectTabPageSizes);
  const pageSize = tabPageSizes[activeTab];
  // Track page index per tab
  const tabPages = useAppSelector(selectTabPages);
  const page = tabPages[activeTab];
  const [forceRefresh, setForceRefresh] = useState(false);
  const loading = useAppSelector(selectGetAllApplicationsLoading);
  const searchLoading = useAppSelector(selectSearchApplicationLoading);
  const response = useAppSelector(selectGetAllApplicationsData);
  const isStale = useAppSelector(selectGetAllApplicationsStale);
  const lastFetchSize = useAppSelector(selectGetAllApplicationsLastFetchSize);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const searchKey = useAppSelector(searchKeyString);
  const searchResult = useAppSelector(selectSearchApplicationData);

  const processData = (allApplications: any) => {
    if (!allApplications) return;
    const applications = allApplications.filter((row: any) => {
      if (
        row.applicationStatus !== Status.Draft &&
        row.applicationStatus !== Status.Complete &&
        row.applicationStatus !== Status.Deleted &&
        row.applicationStatus !== Status.Rejected &&
        row.applicationStatus !== Status.Approved
      )
        return row;
    });
    const approvedApplications = allApplications.filter((row: any) => {
      if (row.applicationStatus === Status.Approved) return row;
    });
    const closedApplications = allApplications.filter((row: any) => {
      if (row.applicationStatus === Status.Complete || row.applicationStatus === Status.Deleted)
        return row;
    });
    const rejectedApplications = allApplications.filter((row: any) => {
      if (row.applicationStatus === Status.Rejected) return row;
    });
    // Filter out Draft status for "All" tab, but include everything else
    const allApplicationsFiltered = allApplications.filter((row: any) => {
      if (row.applicationStatus !== Status.Draft) return row;
    });
    setFilteredApplications(applications);
    setApprovedApplications(approvedApplications);
    setClosedApplications(closedApplications);
    setRejectedApplications(rejectedApplications);
    setAllApplications(allApplicationsFiltered);

    // For dual-role users, default to In Progress tab if there are items awaiting approval
    // Only if we haven't already set a tab (e.g. from Redux state)
    if (isDualRole() && activeTab === 0) {
      const hasAwaitingApproval = applications.some(
        (row: any) => row.applicationStatus === Status.AwaitingApproval,
      );
      if (hasAwaitingApproval) {
        // Stay on In Progress tab (index 0) since we removed Approval Queue
      }
    }
  };

  useEffect(() => {
    // Initial fetch logic if needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Fetch if:
    // 1. Force refresh is triggered
    // 2. Data is stale (e.g. status changed)
    // 3. Last fetch size was small (e.g. from dashboard)
    if (forceRefresh || isStale || lastFetchSize < 1000) {
      const fetchData = async () => {
        const api = postGetAllApplications;
        await dispatch(
          api({ paginationOption: { size: 1000, pageBackwards: false, paginationToken: null } }),
        );
      };
      fetchData().catch(console.error);
    }
  }, [forceRefresh, isStale, lastFetchSize, dispatch]);

  useEffect(() => {
    if (searchResult && searchKey) {
      processData(searchResult.results);
      // Automatically switch to "All" tab when searching
      dispatch(setActiveTab(4));
    } else {
      const reduxResults = response && response.results ? response.results : [];
      processData(reduxResults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult, response, searchKey]);

  // Removed unused searchResults memo

  const rows = useMemo(
    () =>
      (filteredApplications || []).map((row: any, i: number) => {
        if (!filterList.includes(row.applicationStatus)) {
          filterList.push(row.applicationStatus);
        }
        return { ...row, id: i };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }),
    [filteredApplications],
  );

  const approvedRows = useMemo(
    () =>
      (approvedApplications || []).map((row: any, i: number) => {
        return { ...row, id: i };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }),
    [approvedApplications],
  );

  const closedRows = useMemo(
    () =>
      (closedApplications || []).map((row: any, i: number) => {
        return { ...row, id: i };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }),
    [closedApplications],
  );

  const rejectedRows = useMemo(
    () =>
      (rejectedApplications || []).map((row: any, i: number) => {
        return { ...row, id: i };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }),
    [rejectedApplications],
  );

  const allRows = useMemo(
    () =>
      (allApplications || []).map((row: any, i: number) => {
        return { ...row, id: i };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }),
    [allApplications],
  );

  const handleOpen = (data: GridRowModel) => {
    if (data.applicationStatus === 'Draft') {
      navigate('/new-application/supplier', { state: { applicationId: data.applicationId } });
    } else {
      navigate(`/new-application/summary/${data.applicationId}`, {
        state: { applicationId: data.applicationId },
      });
    }
  };

  const allColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'dateLodged',
        headerName: 'Date',
        flex: 0.8,
        minWidth: 110,
        sortable: false,
        align: 'left',
        headerClassName: styles.header,
        valueGetter: (params: GridValueGetterParams) =>
          `${params.row.dateLodged || params.row.dateCreated}`,
        renderCell: (params: GridRenderCellParams<string>) => (
          <Box sx={{ cursor: 'pointer' }} p={1}>
            {moment(params.value).format('DD MMM YYYY')}
          </Box>
        ),
      },
      {
        field: 'applicationId',
        headerName: 'Lodgement #',
        sortable: false,
        flex: 1,
        minWidth: 130,
        align: 'left',
        headerClassName: styles.header,
        renderCell: (params: GridRenderCellParams<string>) => (
          <Box sx={{ cursor: 'pointer' }} p={1}>
            {params.value}
          </Box>
        ),
      },
      {
        field: 'applicant',
        headerName: 'Applicant',
        sortable: false,
        flex: 1.4,
        minWidth: 160,
        align: 'left',
        valueGetter: (params: GridValueGetterParams) =>
          `${params.row.applicantName.fullName || ''}`,
        headerClassName: styles.header,
        renderCell: (params: GridRenderCellParams<string>) => (
          <Box sx={{ cursor: 'pointer' }} p={1}>
            {params.value}
          </Box>
        ),
      },
      {
        field: 'cost',
        headerName: 'Cost',
        sortable: false,
        flex: 0.8,
        minWidth: 100,
        align: 'left',
        headerClassName: styles.header,
        renderCell: (params: GridRenderCellParams<string>) => (
          <Box sx={{ cursor: 'pointer' }} p={1}>
            {dollarFormatter(params.value)}
          </Box>
        ),
      },
      {
        field: 'beneficiary',
        headerName: 'Beneficiary',
        sortable: false,
        flex: 1.4,
        minWidth: 160,
        align: 'left',
        valueGetter: (params: GridValueGetterParams) =>
          `${params.row.beneficiaryName.fullName || ''}`,
        headerClassName: styles.header,
        renderCell: (params: GridRenderCellParams<string>) => (
          <Box sx={{ cursor: 'pointer' }} p={1}>
            {params.value}
          </Box>
        ),
      },
      {
        field: 'applicationStatus',
        headerName: 'Status',
        sortable: false,
        description: 'This column has a value getter and is not sortable.',
        flex: 1.2,
        minWidth: 170,
        align: 'left',
        headerClassName: styles.header,
        renderCell: (params: GridRenderCellParams<string>) => (
          <Box sx={{ cursor: 'pointer' }} p={1} display="flex" alignItems="center">
            <Box>
              <Chip showIcon color={getStatusColor(params.value!)} text={params.value!} />
            </Box>
          </Box>
        ),
      },
      {
        field: 'supplierStatus',
        headerName: 'Supplier Status',
        sortable: false,
        flex: 1.2,
        minWidth: 180,
        align: 'left',
        headerClassName: styles.header,
        renderCell: (params: GridRenderCellParams<string>) => (
          <Box sx={{ cursor: 'pointer' }} p={1}>
            {params.value && params.value !== 'None' && (
              <Chip color={getSupplierStatusColor(params.value)} text={params.value} />
            )}
          </Box>
        ),
      },
      {
        field: 'approvals',
        headerName: 'Approvals',
        sortable: false,
        flex: 0.6,
        minWidth: 100,
        align: 'center',
        headerClassName: styles.header,
        valueGetter: (params: GridValueGetterParams) => {
          // Show approval counts only for applications that have approval requirements
          const required = params.row.requiredApprovals || 0;
          const received = params.row.receivedApprovals || 0;
          return required > 0 ? `${received}/${required}` : '';
        },
        renderCell: (params: GridRenderCellParams<string>) => (
          <Box sx={{ cursor: 'pointer' }} p={1} textAlign="center">
            {params.value || '—'}
          </Box>
        ),
      },
    ],
    [styles.header],
  );

  // Determine if user is MAD internal staff (Admin or Board Member)
  const isMadInternalStaff = hasAdminPermission() || hasMemberPermission();

  // Filter columns based on active tab and user permissions
  const columns = useMemo(() => {
    let filteredColumns = allColumns;

    // Hide Supplier Status on In Progress tab
    if (activeTab === 0) {
      filteredColumns = filteredColumns.filter((col) => col.field !== 'supplierStatus');
    }

    // Hide Supplier Status and Approvals for practitioners and beneficiaries
    // Only show to Admins and Board Members
    if (!isMadInternalStaff) {
      filteredColumns = filteredColumns.filter(
        (col) => col.field !== 'supplierStatus' && col.field !== 'approvals',
      );
    }

    return filteredColumns;
  }, [activeTab, allColumns, isMadInternalStaff]);

  const dualRole = isDualRole();

  // Removed unused handleTabProgressChange

  // When tab changes, restore its last page
  const handleTabChange = (_: React.SyntheticEvent, tabIndex: number) => {
    dispatch(setActiveTab(tabIndex));
  };

  // When page changes, update for current tab
  const handlePageChange = (newPage: number) => {
    dispatch(setPageForTab({ tabIndex: activeTab, page: newPage }));
  };

  // When pageSize changes, update for current tab
  const handlePageSizeChange = (newPageSize: number) => {
    dispatch(setPageSizeForTab({ tabIndex: activeTab, pageSize: newPageSize }));
  };

  return (
    <Box height="95%" minHeight={`calc(100vh - 65px)`}>
      <Box display="flex" height="100%">
        <Box
          sx={{ pl: { xs: 1.5, sm: 12 }, pb: 6, pr: { xs: 3, sm: 4 }, pt: 4 }}
          display="flex"
          flexDirection="column"
          width="100%"
        >
          <Grid direction="column" sx={{ flexWrap: 'nowrap' }} container>
            <Grid sx={{ width: '100%', px: 0 }} item>
              <Box width="100%" display="flex" justifyContent="space-between">
                <Box>
                  <Typography color="primary" variant="h2">
                    {hasPractitionerPermission() ? 'My Applications' : 'All Applications'}
                    <Tooltip title="Refresh List">
                      <IconButton
                        onClick={() => setForceRefresh((prev) => !prev)}
                        size="small"
                        sx={{ ml: 1 }}
                      >
                        <Refresh fontSize="small" color="action" />
                      </IconButton>
                    </Tooltip>
                  </Typography>
                  {/* <Typography style={{ maxWidth: 560 }} variant="h6" className={styles.label}>
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vivamus commodo
                    hendrerit mauris ut consectetur.
                  </Typography> */}
                </Box>
                {(hasPractitionerPermission() || hasAdminPermission()) && (
                  <Box>
                    <Button
                      onClick={() => navigate('/new-application/supplier')}
                      variant="contained"
                      color="primary"
                      sx={{
                        height: 43,
                        px: 4,
                      }}
                    >
                      New Application
                    </Button>
                  </Box>
                )}
              </Box>
              <Box mt={5}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  {dualRole ? (
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      aria-label="basic tabs example"
                    >
                      <Tab sx={{ textTransform: 'none', fontWeight: 600 }} label="In Progress" />
                      <Tab sx={{ textTransform: 'none', fontWeight: 600 }} label="Approved" />
                      <Tab sx={{ textTransform: 'none', fontWeight: 600 }} label="Closed" />
                      <Tab sx={{ textTransform: 'none', fontWeight: 600 }} label="Rejected" />
                      <Tab sx={{ textTransform: 'none', fontWeight: 600 }} label="All" />
                    </Tabs>
                  ) : (
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      aria-label="basic tabs example"
                    >
                      <Tab sx={{ textTransform: 'none', fontWeight: 600 }} label="In Progress" />
                      <Tab sx={{ textTransform: 'none', fontWeight: 600 }} label="Approved" />
                      <Tab sx={{ textTransform: 'none', fontWeight: 600 }} label="Closed" />
                      <Tab sx={{ textTransform: 'none', fontWeight: 600 }} label="Rejected" />
                      <Tab sx={{ textTransform: 'none', fontWeight: 600 }} label="All" />
                    </Tabs>
                  )}
                </Box>
                {loading === 'loading' || searchLoading === 'loading' ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: 500,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {dualRole ? (
                      <>
                        <TabPanel value={activeTab} index={0}>
                          {isMobile ? (
                            <MobileGrid
                              rows={rows}
                              onRowClick={handleOpen}
                              statusKey="applicationStatus"
                            />
                          ) : (
                            <DataGrid
                              rows={rows}
                              columns={columns}
                              pageSize={pageSize}
                              onPageSizeChange={handlePageSizeChange}
                              rowsPerPageOptions={[5, 10, 25, 50]}
                              autoHeight
                              onRowClick={(data) => handleOpen(data.row)}
                              disableSelectionOnClick
                              experimentalFeatures={{ newEditingApi: true }}
                              page={page}
                              onPageChange={handlePageChange}
                            />
                          )}
                        </TabPanel>
                        <TabPanel value={activeTab} index={1}>
                          {isMobile ? (
                            <MobileGrid
                              rows={approvedRows}
                              onRowClick={handleOpen}
                              statusKey="applicationStatus"
                            />
                          ) : (
                            <DataGrid
                              rows={approvedRows}
                              columns={columns}
                              pageSize={pageSize}
                              onPageSizeChange={handlePageSizeChange}
                              rowsPerPageOptions={[5, 10, 25, 50]}
                              autoHeight
                              onRowClick={(data) => handleOpen(data.row)}
                              disableSelectionOnClick
                              experimentalFeatures={{ newEditingApi: true }}
                              page={page}
                              onPageChange={handlePageChange}
                            />
                          )}
                        </TabPanel>
                        <TabPanel value={activeTab} index={2}>
                          {isMobile ? (
                            <MobileGrid
                              rows={closedRows}
                              onRowClick={handleOpen}
                              statusKey="applicationStatus"
                            />
                          ) : (
                            <DataGrid
                              rows={closedRows}
                              columns={columns}
                              pageSize={pageSize}
                              onPageSizeChange={handlePageSizeChange}
                              rowsPerPageOptions={[5, 10, 25, 50]}
                              autoHeight
                              onRowClick={(data) => handleOpen(data.row)}
                              disableSelectionOnClick
                              experimentalFeatures={{ newEditingApi: true }}
                              page={page}
                              onPageChange={handlePageChange}
                            />
                          )}
                        </TabPanel>
                        <TabPanel value={activeTab} index={3}>
                          {isMobile ? (
                            <MobileGrid
                              rows={rejectedRows}
                              onRowClick={handleOpen}
                              statusKey="applicationStatus"
                            />
                          ) : (
                            <DataGrid
                              rows={rejectedRows}
                              columns={columns}
                              pageSize={pageSize}
                              onPageSizeChange={handlePageSizeChange}
                              rowsPerPageOptions={[5, 10, 25, 50]}
                              autoHeight
                              onRowClick={(data) => handleOpen(data.row)}
                              disableSelectionOnClick
                              experimentalFeatures={{ newEditingApi: true }}
                              page={page}
                              onPageChange={handlePageChange}
                            />
                          )}
                        </TabPanel>
                        <TabPanel value={activeTab} index={4}>
                          {isMobile ? (
                            <MobileGrid
                              rows={allRows}
                              onRowClick={handleOpen}
                              statusKey="applicationStatus"
                            />
                          ) : (
                            <DataGrid
                              rows={allRows}
                              columns={columns}
                              pageSize={pageSize}
                              onPageSizeChange={handlePageSizeChange}
                              rowsPerPageOptions={[5, 10, 25, 50]}
                              autoHeight
                              onRowClick={(data) => handleOpen(data.row)}
                              disableSelectionOnClick
                              experimentalFeatures={{ newEditingApi: true }}
                              page={page}
                              onPageChange={handlePageChange}
                            />
                          )}
                        </TabPanel>
                      </>
                    ) : (
                      <>
                        <TabPanel value={activeTab} index={0}>
                          {isMobile ? (
                            <MobileGrid
                              rows={rows}
                              onRowClick={handleOpen}
                              statusKey="applicationStatus"
                            />
                          ) : (
                            <DataGrid
                              rows={rows}
                              columns={columns}
                              pageSize={10}
                              rowsPerPageOptions={[5]}
                              autoHeight
                              onRowClick={(data) => handleOpen(data.row)}
                              disableSelectionOnClick
                              experimentalFeatures={{ newEditingApi: true }}
                              page={page}
                              onPageChange={handlePageChange}
                              sx={{
                                '& .MuiDataGrid-row': {
                                  '& .MuiDataGrid-menuIconButton': {
                                    visibility: 'visible',
                                    opacity: 1,
                                    color: '#1976d2',
                                  },
                                  '&:hover .MuiDataGrid-menuIconButton': {
                                    color: '#1565c0',
                                  },
                                },
                                '& .MuiDataGrid-menuIconButton': {
                                  visibility: 'visible !important',
                                  opacity: '1 !important',
                                  color: '#1976d2 !important',
                                },
                              }}
                            />
                          )}
                        </TabPanel>
                        <TabPanel value={activeTab} index={1}>
                          {isMobile ? (
                            <MobileGrid
                              rows={approvedRows}
                              onRowClick={handleOpen}
                              statusKey="applicationStatus"
                            />
                          ) : (
                            <DataGrid
                              rows={approvedRows}
                              columns={columns}
                              pageSize={10}
                              rowsPerPageOptions={[5]}
                              autoHeight
                              onRowClick={(data) => handleOpen(data.row)}
                              disableSelectionOnClick
                              experimentalFeatures={{ newEditingApi: true }}
                              page={page}
                              onPageChange={handlePageChange}
                              sx={{
                                '& .MuiDataGrid-row': {
                                  '& .MuiDataGrid-menuIconButton': {
                                    visibility: 'visible',
                                    opacity: 1,
                                    color: '#1976d2',
                                  },
                                  '&:hover .MuiDataGrid-menuIconButton': {
                                    color: '#1565c0',
                                  },
                                },
                                '& .MuiDataGrid-menuIconButton': {
                                  visibility: 'visible !important',
                                  opacity: '1 !important',
                                  color: '#1976d2 !important',
                                },
                              }}
                            />
                          )}
                        </TabPanel>
                        <TabPanel value={activeTab} index={2}>
                          {isMobile ? (
                            <MobileGrid
                              rows={closedRows}
                              onRowClick={handleOpen}
                              statusKey="applicationStatus"
                            />
                          ) : (
                            <DataGrid
                              rows={closedRows}
                              columns={columns}
                              pageSize={10}
                              rowsPerPageOptions={[5]}
                              autoHeight
                              onRowClick={(data) => handleOpen(data.row)}
                              disableSelectionOnClick
                              experimentalFeatures={{ newEditingApi: true }}
                              page={page}
                              onPageChange={handlePageChange}
                              sx={{
                                '& .MuiDataGrid-row': {
                                  '& .MuiDataGrid-menuIconButton': {
                                    visibility: 'visible',
                                    opacity: 1,
                                    color: '#1976d2',
                                  },
                                  '&:hover .MuiDataGrid-menuIconButton': {
                                    color: '#1565c0',
                                  },
                                },
                                '& .MuiDataGrid-menuIconButton': {
                                  visibility: 'visible !important',
                                  opacity: '1 !important',
                                  color: '#1976d2 !important',
                                },
                              }}
                            />
                          )}
                        </TabPanel>
                        <TabPanel value={activeTab} index={3}>
                          {isMobile ? (
                            <MobileGrid
                              rows={rejectedRows}
                              onRowClick={handleOpen}
                              statusKey="applicationStatus"
                            />
                          ) : (
                            <DataGrid
                              rows={rejectedRows}
                              columns={columns}
                              pageSize={10}
                              rowsPerPageOptions={[5]}
                              autoHeight
                              onRowClick={(data) => handleOpen(data.row)}
                              disableSelectionOnClick
                              experimentalFeatures={{ newEditingApi: true }}
                              page={page}
                              onPageChange={handlePageChange}
                              sx={{
                                '& .MuiDataGrid-row': {
                                  '& .MuiDataGrid-menuIconButton': {
                                    visibility: 'visible',
                                    opacity: 1,
                                    color: '#1976d2',
                                  },
                                  '&:hover .MuiDataGrid-menuIconButton': {
                                    color: '#1565c0',
                                  },
                                },
                                '& .MuiDataGrid-menuIconButton': {
                                  visibility: 'visible !important',
                                  opacity: '1 !important',
                                  color: '#1976d2 !important',
                                },
                              }}
                            />
                          )}
                        </TabPanel>
                        <TabPanel value={activeTab} index={4}>
                          {isMobile ? (
                            <MobileGrid
                              rows={allRows}
                              onRowClick={handleOpen}
                              statusKey="applicationStatus"
                            />
                          ) : (
                            <DataGrid
                              rows={allRows}
                              columns={columns}
                              pageSize={10}
                              rowsPerPageOptions={[5]}
                              autoHeight
                              onRowClick={(data) => handleOpen(data.row)}
                              disableSelectionOnClick
                              experimentalFeatures={{ newEditingApi: true }}
                              page={page}
                              onPageChange={handlePageChange}
                              sx={{
                                '& .MuiDataGrid-row': {
                                  '& .MuiDataGrid-menuIconButton': {
                                    visibility: 'visible',
                                    opacity: 1,
                                    color: '#1976d2',
                                  },
                                  '&:hover .MuiDataGrid-menuIconButton': {
                                    color: '#1565c0',
                                  },
                                },
                                '& .MuiDataGrid-menuIconButton': {
                                  visibility: 'visible !important',
                                  opacity: '1 !important',
                                  color: '#1976d2 !important',
                                },
                              }}
                            />
                          )}
                        </TabPanel>
                      </>
                    )}
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Toast
        loading={loading}
        response={response}
        open={open}
        setOpen={setOpen}
        successMsg="Successfully loaded applications"
      />
    </Box>
  );
};

export default ApplicationTracking;
